import { PROXY_FORM_FIELDS } from '../DeviceComponents/Metas/ProxyMeta';
import {
  FormAttribute,
  AttributeMeta,
  FormErrors,
  FormMeta,
  FormState,
  AndroidOSFormState,
  AppleOSFormState
} from '../types/interface';
import { isConditionMet } from './MdmUtils';
import { validateForm, preFormatForm } from '@nile/components/src/Validators';
import { getTranslatedText } from '@nile/components';
import { validate } from 'validate.js';
import { ANDROID_OS_UPDATE_TYPE } from '../Constants';
import {
  OSUpdateEnforcement,
  VersionEnforcement,
  OSUpdateInput
} from '../types/interface';

/**
 * Validation utility function for individual field
 */
const validateField = (
  attribute: FormAttribute,
  value: boolean | string | number,
  _intl?: any // Optional intl object for translations
): string | null => {
  const stringValue = String(value);

  // First Priority: Mandatory field validation
  if (attribute.uiIsMandatory) {
    // Check if value is present for mandatory fields
    if (['text', 'email', 'number', 'select'].includes(attribute.uiDataType)) {
      if (
        !stringValue ||
        stringValue.trim() === '' ||
        (attribute.uiDataType === 'number' &&
          (value === null || value === undefined))
      ) {
        return `${attribute.uiLabel} is required`;
      }
    }
  }

  if (
    attribute.uiDataType === 'email' &&
    stringValue &&
    stringValue.trim() !== ''
  ) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(stringValue)) {
      return getTranslatedText(_intl, 'common.message.enterValidEmail');
    }
  }

  // ArrayList validation
  if (attribute.uiDataType === 'arrayList') {
    if (attribute.uiIsMandatory) {
      if (!stringValue || stringValue.trim() === '') {
        return `${attribute.uiLabel} is required`;
      }

      // Check if at least one non-empty item exists after splitting
      const arrayItems = stringValue
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item !== '');
      if (arrayItems.length === 0) {
        return `${attribute.uiLabel} must contain at least one value`;
      }

      // Check for comma in individual items (should not happen with proper UI, but safety check)
      const itemsWithComma = arrayItems.filter((item) => item.includes(','));
      if (itemsWithComma.length > 0) {
        return `${attribute.uiLabel} items cannot contain commas`;
      }

      // Validate each array item against regex if provided
      if (attribute.uiMatchRegex) {
        const regex = new RegExp(attribute.uiMatchRegex);
        const invalidItems = arrayItems.filter((item) => !regex.test(item));
        if (invalidItems.length > 0) {
          return `${
            attribute.uiLabel
          } contains invalid format items: ${invalidItems.join(', ')}`;
        }
      }
    }
    // If not mandatory but has value, still check for commas and regex
    else if (stringValue && stringValue.trim() !== '') {
      const arrayItems = stringValue
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item !== '');

      // Check for comma in individual items
      const itemsWithComma = arrayItems.filter((item) => item.includes(','));
      if (itemsWithComma.length > 0) {
        return `${attribute.uiLabel} items cannot contain commas`;
      }

      // Validate each array item against regex if provided
      if (attribute.uiMatchRegex) {
        const regex = new RegExp(attribute.uiMatchRegex);
        const invalidItems = arrayItems.filter(
          (item) => item.trim() !== '' && !regex.test(item)
        );
        if (invalidItems.length > 0) {
          return `${
            attribute.uiLabel
          } contains invalid format items: ${invalidItems.join(', ')}`;
        }
      }
    }
  }

  // Second Priority: Min/Max validation for number type
  if (attribute.uiDataType === 'number' || attribute.dataType === 'number') {
    // For mandatory fields, we've already checked if value exists above
    // For non-mandatory fields, only validate if value is provided
    if (stringValue && stringValue.trim() !== '') {
      const numericValue = Number(stringValue);

      if (isNaN(numericValue)) {
        return `${attribute.uiLabel} must be a valid number`;
      }

      if (
        attribute.uiMinValue !== null &&
        attribute.uiMinValue !== undefined &&
        numericValue < attribute.uiMinValue
      ) {
        return `${attribute.uiLabel} must be at least ${attribute.uiMinValue}`;
      }

      if (
        attribute.uiMaxValue !== null &&
        attribute.uiMaxValue !== undefined &&
        numericValue > attribute.uiMaxValue
      ) {
        return `${attribute.uiLabel} must not exceed ${attribute.uiMaxValue}`;
      }
    }
  }

  // Third Priority: Regex validation for email and other data types
  if (attribute.uiMatchRegex && stringValue && stringValue.trim() !== '') {
    const regex = new RegExp(attribute.uiMatchRegex);
    if (!regex.test(stringValue)) {
      // Special handling for email validation
      if (attribute.uiDataType === 'email') {
        return `${attribute.uiLabel} must be a valid email address`;
      }
      return `${attribute.uiLabel} format is invalid`;
    }
  }

  return null;
};

/**
 * Validation utility function for all form values
 */
const validateFormValues = (
  metaData: AttributeMeta,
  values: Record<string, boolean | string | number>,
  _intl?: any
): FormErrors => {
  const errors: FormErrors = {};

  const validateAttributes = (
    attributes: FormAttribute[],
    parentEnabled: boolean = true
  ) => {
    attributes.forEach((attribute) => {
      const value = values[attribute.attributePath] ?? attribute.uiDefaultValue;

      // Only validate if parent is enabled or this is a top-level attribute
      if (parentEnabled) {
        const error = validateField(attribute, value, _intl);

        if (error) {
          errors[attribute.attributePath] = error;
        } else {
          errors[attribute.attributePath] = null;
        }

        // For child attributes, check if this toggle/checkbox is enabled
        if (
          attribute.dependentAttributes &&
          attribute.dependentAttributes.length > 0
        ) {
          const isParentEnabled =
            attribute.uiDataType === 'toggle' ||
            attribute.uiDataType === 'checkbox'
              ? Boolean(value)
              : true;
          validateAttributes(attribute.dependentAttributes, isParentEnabled);
        }
      } else {
        // If parent is disabled, clear any existing errors for this attribute and its children
        errors[attribute.attributePath] = null;

        // Recursively clear errors for child attributes
        if (
          attribute.dependentAttributes &&
          attribute.dependentAttributes.length > 0
        ) {
          const clearChildErrors = (childAttributes: FormAttribute[]) => {
            childAttributes.forEach((childAttr) => {
              errors[childAttr.attributePath] = null;
              if (
                childAttr.dependentAttributes &&
                childAttr.dependentAttributes.length > 0
              ) {
                clearChildErrors(childAttr.dependentAttributes);
              }
            });
          };
          clearChildErrors(attribute.dependentAttributes);
        }
      }
    });
  };

  // Validate all sections
  metaData.sections.forEach((section) => {
    validateAttributes(section.attributes, true);
  });

  return errors;
};

/**
 * Helper function to check if form has any validation errors
 */
const hasFormErrors = (errors: FormErrors): boolean => {
  return Object.values(errors).some((error) => error !== null);
};

/**
 * Helper function to get first error message from form errors
 */
const getFirstFormError = (errors: FormErrors): string | null => {
  const firstError = Object.values(errors).find((error) => error !== null);
  return firstError || null;
};

validate.validators.regexChecker = function(value: string, options: any) {
  if (!value) return; // skip empty (presence handles required)

  const { regex, message } = options;
  if (!regex.test(value)) {
    return message; // must return the localized message
  }
};

/**
 * Build validate.js constraints from FormMeta field
 */
const buildFieldConstraints = (
  field: FormMeta,
  allValues: FormState,
  intl?: any
): any => {
  const { validation } = field;
  if (!validation) return {};

  const constraints: any = {};

  // Handle conditional validation
  let isFieldRequired = field.required;
  if (validation.conditionalRule) {
    const conditionMet = isConditionMet(validation.conditionalRule, allValues);
    if (conditionMet) {
      isFieldRequired = validation.conditionalRule.required || false;
    } else {
      isFieldRequired = false;
    }
  } else if (validation.required) {
    isFieldRequired = true;
  }
  const translatedLabel = getTranslatedText(intl, field.label);

  // Required field validation
  if (isFieldRequired) {
    constraints.presence = {
      message: `^${getTranslatedText(intl, 'common.validation.required', {
        labelName: translatedLabel
      })}`
    };
  }

  // Pattern validation using regexChecker
  if (validation.pattern) {
    constraints.regexChecker = {
      regex: new RegExp(validation.pattern),
      mandatory: false,
      message: `^${getTranslatedText(intl, 'common.validation.not.valid', {
        labelName: translatedLabel
      })}`
    };
  }

  // Number range validation
  if (field.type === 'number') {
    constraints.numericality = {};
    if (validation.min !== undefined) {
      constraints.numericality.greaterThanOrEqualTo = validation.min;
      constraints.numericality.notGreaterThanOrEqualTo = `^${getTranslatedText(
        intl,
        'common.validation.minimum',
        { value: validation.min }
      )}`;
    }
    if (validation.max !== undefined) {
      constraints.numericality.lessThanOrEqualTo = validation.max;
      constraints.numericality.notLessThanOrEqualTo = `^${getTranslatedText(
        intl,
        'common.validation.maximum',
        { value: validation.max }
      )}`;
    }
  }

  return constraints;
};

/**
 * Build validate.js constraints object from FormMeta fields
 */
const buildFormConstraints = (
  fields: FormMeta[],
  formValues: FormState,
  intl?: any
): any => {
  const constraints: any = {};

  fields.forEach((field) => {
    // Skip validation for fields that are not visible due to conditions
    if (field.condition && !isConditionMet(field.condition, formValues)) {
      return;
    }

    const fieldConstraints = buildFieldConstraints(field, formValues, intl);
    if (Object.keys(fieldConstraints).length > 0) {
      constraints[field.name] = fieldConstraints;
    }
  });

  return constraints;
};

/**
 * Utility function to determine if a field is currently required based on conditional rules
 */
const isFieldRequired = (field: FormMeta, formValues: FormState): boolean => {
  const { validation } = field;

  // Start with the base required value
  let fieldRequired = field.required;

  // Check conditional validation rules
  if (validation?.conditionalRule) {
    const conditionMet = isConditionMet(validation.conditionalRule, formValues);
    if (conditionMet) {
      fieldRequired = validation.conditionalRule.required || false;
    } else {
      fieldRequired = false;
    }
  } else if (validation?.required) {
    fieldRequired = true;
  }

  return fieldRequired;
};

/**
 * Common validation function for FormMeta form using validate.js
 */
const validateFormMetaForm = (
  fields: FormMeta[],
  formValues: FormState,
  intl?: any
): FormErrors => {
  const constraints = buildFormConstraints(fields, formValues, intl);

  if (Object.keys(constraints).length === 0) {
    return {};
  }

  const preFormattedData = preFormatForm(formValues);
  const validationErrors = validateForm(preFormattedData, constraints) || {};

  // Convert validate.js error format to our FormErrors format
  const errors: FormErrors = {};
  Object.keys(validationErrors).forEach((fieldName) => {
    const fieldErrors = validationErrors[fieldName];
    if (fieldErrors && fieldErrors.length > 0) {
      errors[fieldName] = fieldErrors[0]; // Take the first error
    }
  });

  return errors;
};

/**
 * Validates OS Update Input (for minor and major updates)
 */
const validateOSUpdateInput = (
  input: OSUpdateInput | null,
  updateType: 'minor' | 'major',
  intl?: any
): FormErrors => {
  const errors: FormErrors = {};
  const prefix = updateType === 'minor' ? 'minorOsUpdate' : 'majorOsUpdate';

  if (!input) {
    return errors;
  }

  // Validate afterDays
  if (input.afterDays === null || input.afterDays === undefined) {
    errors[`${prefix}AfterDays`] = getTranslatedText(
      intl,
      'common.validation.required',
      { labelName: getTranslatedText(intl, 'attribute.days') }
    );
  } else if (input.afterDays < 1) {
    errors[`${prefix}AfterDays`] = getTranslatedText(
      intl,
      'common.validation.minimum',
      { value: 1 }
    );
  } else if (input.afterDays > 90) {
    errors[`${prefix}AfterDays`] = getTranslatedText(
      intl,
      'common.validation.maximum',
      { value: 90 }
    );
  }

  // Validate time
  if (!input.time) {
    errors[`${prefix}Time`] = getTranslatedText(
      intl,
      'common.validation.required',
      { labelName: getTranslatedText(intl, 'client.new.workingtime.time') }
    );
  }

  return errors;
};

/**
 * Validates Version Enforcement
 */
const validateVersionEnforcements = (
  versionEnforcements: VersionEnforcement[] | null,
  intl?: any
): FormErrors => {
  const errors: FormErrors = {};

  if (!versionEnforcements || versionEnforcements.length === 0) {
    return errors;
  }

  // Filter out removed items for validation
  const activeEnforcements = versionEnforcements.filter(
    (enforcement) => !enforcement.isRemoved
  );

  activeEnforcements.forEach((enforcement) => {
    const prefix = `versionEnforcement_${enforcement.versionEnforcementId}`;

    // Validate target version
    if (!enforcement.targetVersion) {
      errors[`${prefix}_targetVersion`] = getTranslatedText(
        intl,
        'common.validation.required',
        { labelName: 'Enforce version' } // TODO: Add localization
      );
    }

    // Validate target date time
    if (!enforcement.targetLocalDateTime) {
      errors[`${prefix}_targetLocalDateTime`] = getTranslatedText(
        intl,
        'common.validation.required',
        { labelName: 'Enforcement deadline' } // TODO: Add localization
      );
    }
  });

  return errors;
};

/**
 * Validates Software Update Enforcement
 */
const validateSoftwareUpdateEnforcement = (
  enforcement: OSUpdateEnforcement,
  intl?: any
): FormErrors => {
  const errors: FormErrors = {};

  if (!enforcement || enforcement.enforcementType === 'NONE') {
    return errors;
  }

  // Validate minor OS update input
  if (enforcement.enforcementType === 'LATEST_MINOR_UPDATE') {
    const minorErrors = validateOSUpdateInput(
      enforcement.minorOsUpdateEnforcement,
      'minor',
      intl
    );
    Object.assign(errors, minorErrors);
  }

  // Validate major OS update input
  if (enforcement.enforcementType === 'LATEST_MAJOR_UPGRADE') {
    const majorErrors = validateOSUpdateInput(
      enforcement.majorOsUpdateEnforcement,
      'major',
      intl
    );
    Object.assign(errors, majorErrors);
  }

  // Validate version enforcements
  if (enforcement.enforcementType === 'SPECIFIC_VERSION') {
    const versionErrors = validateVersionEnforcements(
      enforcement.osUpdateVersionEnforcements,
      intl
    );
    Object.assign(errors, versionErrors);
  }

  return errors;
};

const validateProxyForm = (formValues: FormState, intl?: any): FormErrors => {
  return validateFormMetaForm(PROXY_FORM_FIELDS, formValues, intl);
};

/**
 * Validates time window for Android OS updates
 */
const validateTimeWindow = (
  startTime: string,
  endTime: string,
  intl?: any
): string | null => {
  if (!startTime || !endTime) {
    return getTranslatedText(intl, 'mdm.validation.time.window.required');
  }

  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);

  const startMins = sh * 60 + sm;
  const endMins = eh * 60 + em;

  if (startMins >= endMins) {
    return getTranslatedText(intl, 'mdm.validation.time.window.invalid');
  }

  return null;
};

/**
 * Validates individual time field
 */
const validateTimeField = (
  time: string | null,
  fieldName: string,
  intl?: any
): string | null => {
  if (!time) {
    return getTranslatedText(intl, 'common.validation.required', {
      labelName: fieldName
    });
  }
  return null;
};

/**
 * Validates Android OS form values with separate errors for start and end time
 */
const validateAndroidFormValues = (
  formValues: AndroidOSFormState,
  intl?: any
): FormErrors => {
  const errors: FormErrors = {};

  // Validate windowed update time window
  if (formValues.updateType === ANDROID_OS_UPDATE_TYPE.WINDOWED) {
    const { startTime, endTime } = formValues.updateWindow || {};

    // Check for empty start time
    const startTimeError = validateTimeField(startTime, 'Start time', intl);
    if (startTimeError) {
      errors.startTime = startTimeError;
    }

    // Check for empty end time
    const endTimeError = validateTimeField(endTime, 'End time', intl);
    if (endTimeError) {
      errors.endTime = endTimeError;
    }

    // If both times are provided, check if start time is greater than end time
    if (startTime && endTime) {
      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = endTime.split(':').map(Number);

      const startMins = sh * 60 + sm;
      const endMins = eh * 60 + em;

      if (startMins >= endMins) {
        errors.timeWindow = getTranslatedText(
          intl,
          'mdm.validation.time.start.greater.than.end'
        );
      }
    }
  }

  return errors;
};

/**
 * Validates Apple OS form values
 */
const validateAppleFormValues = (
  formValues: AppleOSFormState,
  intl?: any
): FormErrors => {
  const errors: FormErrors = {};

  // Validate deferral days
  const deferralDays = formValues?.deferrals?.combinedPeriodInDays;
  if (deferralDays !== undefined) {
    if (deferralDays < 1) {
      errors.deferralDays = getTranslatedText(
        intl,
        'common.validation.minimum',
        { value: 1 }
      );
    } else if (deferralDays > 9) {
      errors.deferralDays = getTranslatedText(
        intl,
        'common.validation.maximum',
        { value: 9 }
      );
    }
  }

  // Validate software update enforcement
  if (formValues?.enforcement) {
    const enforcementErrors = validateSoftwareUpdateEnforcement(
      formValues.enforcement,
      intl
    );
    Object.assign(errors, enforcementErrors);
  }

  return errors;
};

export {
  validateField,
  validateFormValues,
  hasFormErrors,
  getFirstFormError,
  buildFieldConstraints,
  buildFormConstraints,
  isFieldRequired,
  validateFormMetaForm,
  validateProxyForm,
  validateTimeWindow,
  validateTimeField,
  validateAndroidFormValues,
  validateAppleFormValues,
  validateOSUpdateInput,
  validateVersionEnforcements,
  validateSoftwareUpdateEnforcement
};
